import { z } from 'zod';

import { BlockValueTemplateHint, validateBlockValueTemplate } from './template.js';

export enum BlockValueFieldSchemaVariant {
  TemplateAndPlainString = 'TemplateAndPlainString',
  TemplateOnly = 'TemplateOnly',
  OptionalTemplate = 'OptionalTemplate',
}
interface BlockValueFieldSchemaProps {
  // Default is TemplateAndPlainString
  variant: BlockValueFieldSchemaVariant;
}
interface BlockValueFieldSchemaV2Props {
  variant: Exclude<BlockValueFieldSchemaVariant, 'TemplateAndPlainString'>;
}

export type BlockValue = `{{${string}}}`;
/**
 * A Zod schema for validating block value template strings with variant fallback to plain string.
 * e.g., `{{blockName.data.field}}`
 *
 * The schema validates strings based on the provided `variant`:
 * - `TemplateAndPlainString` <Default>: Accepts both valid template strings and plain strings.
 * - `TemplateOnly`: Accepts only valid template strings.
 * - `OptionalTemplate`: Accepts valid template strings or an empty string.
 */
export function BlockValueFieldSchema(props?: BlockValueFieldSchemaProps) {
  return z.string().superRefine((value, context) => {
    const validationResult = validateBlockValueTemplate(value);
    if (validationResult.hint === BlockValueTemplateHint.Valid) {
      return;
    }
    if ('message' in validationResult) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: validationResult.message,
      });
    }
    if (validationResult.hint === BlockValueTemplateHint.NotTemplateLike) {
      const { variant = BlockValueFieldSchemaVariant.TemplateAndPlainString } = props ?? {};
      switch (variant) {
        case BlockValueFieldSchemaVariant.TemplateAndPlainString:
          return;
        case BlockValueFieldSchemaVariant.OptionalTemplate:
          if (value === '') {
            return;
          }
          // Simulate fall through (duplicated code in order to pass linter)
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be a valid interpolated variable. e.g. {{blockName.data.field}}',
          });
          return;
        case BlockValueFieldSchemaVariant.TemplateOnly:
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be a valid interpolated variable. e.g. {{blockName.data.field}}',
          });
          return;
        default:
          throw new Error(`Unknown variant: ${variant}`);
      }
    }
  });
}

/**
 * A Zod schema for validating block value template strings with variant fallback to plain string.
 * e.g., `{{blockName.data.field}}`
 *
 * The schema validates strings based on the provided `variant`:
 * - `TemplateOnly`: Accepts only valid template strings.
 * - `OptionalTemplate`: Accepts valid template strings or an empty string.
 */
export function BlockValueFieldSchemaV2(props: {
  variant: BlockValueFieldSchemaVariant.TemplateOnly;
}): z.ZodString;
export function BlockValueFieldSchemaV2(props: {
  variant: BlockValueFieldSchemaVariant.OptionalTemplate;
}): z.ZodUnion<[z.ZodString, z.ZodLiteral<''>]>;
export function BlockValueFieldSchemaV2(props: BlockValueFieldSchemaV2Props) {
  const { variant } = props;
  const blockValueSchema = z.string().superRefine((value, context) => {
    const validationResult = validateBlockValueTemplate(value);
    if (validationResult.hint === BlockValueTemplateHint.Valid) {
      return z.NEVER;
    }
    if ('message' in validationResult) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: validationResult.message,
      });
      return z.NEVER;
    }
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Must be a valid interpolated variable. e.g. {{blockName.data.field}}',
    });
    return z.NEVER;
  });
  if (variant === BlockValueFieldSchemaVariant.TemplateOnly) {
    return blockValueSchema;
  }
  return z.union([blockValueSchema, z.literal('')]);
}
